import React, { useRef, useState } from "react";
import { Box, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import CenteredLoader from "@components/CenteredLoader";
import { InactivityHandler } from "@components/InactivityHandler";
import CustomRightImage from "@components/CustomRightImagev2";
import CardInformacionMoto from "@components/CardInformacionMoto";
import { MotorbikeCatalogItem } from "@interfaces/catalogs";
import { useLazyGetMotorbikeCatalogQuery } from "@api/catalogs";
import { useDispatch } from "react-redux";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { ApiError } from "@interfaces/response";
import * as Sentry from "@sentry/react";
import { useNavigate } from "react-router-dom";
import { TaxSystemConfigResponse } from "@interfaces/response";
import { useLazyGetTaxSystemConfigQuery } from "@api/applications";
import { generateUrlPortal } from "@helpers/funciones";

const TAXSYTEM_CODE_PFAE = "CSCM2";

const SeleccionarMoto = () => {
  // hooks
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  // api hooks
  const [getTaxSystemConfig] = useLazyGetTaxSystemConfigQuery();
  const [getMotorbikeCatalog] = useLazyGetMotorbikeCatalogQuery();

  // state
  const [activeIndex, setActiveIndex] = useState(0);
  const [taxSystemConfig, setTaxSystemConfig] = useState<TaxSystemConfigResponse | null>(null);
  const [motorbikeCatalog, setMotorbikeCatalog] = useState<MotorbikeCatalogItem[]>([]);
  const [isFetchingCatalog, setIsFetchingCatalog] = useState(true);

  const handleScroll = () => {
    if (!scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    const firstChild = container.firstElementChild as HTMLElement | null;
    if (!firstChild) return;

    const cardFullWidth = firstChild.offsetWidth + 16;
    const index = Math.round(container.scrollLeft / cardFullWidth);
    const safeIndex = Math.max(0, Math.min(index, motorbikeCatalog.length - 1));
    setActiveIndex(safeIndex);
  };

  const fetchData = React.useCallback(async () => {
    setIsFetchingCatalog(true);
    try {
      const [taxSystemConfigResp, motorbikeCatalogResp] = await Promise.all([
        getTaxSystemConfig(TAXSYTEM_CODE_PFAE).unwrap(),
        getMotorbikeCatalog().unwrap(),
      ]);

      setTaxSystemConfig(taxSystemConfigResp);
      setMotorbikeCatalog(motorbikeCatalogResp);
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          component: "SeleccionarMoto-fetchData",
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsFetchingCatalog(false);
    }
  }, [getMotorbikeCatalog, getTaxSystemConfig, dispatch]);

  const handleSelectMotorbike = (selectedMotorbike: MotorbikeCatalogItem) => {
    if (!taxSystemConfig) {
      dispatch(setModalErrorVisible({ open: true, error: null }));
      return;
    }

    const urlPortal = generateUrlPortal({
      taxSystemConfig,
      loan_amount: selectedMotorbike.data?.price || 0,
      loan_term: taxSystemConfig.defaultTerm,
      motorbike_catalog_id: selectedMotorbike.id,
    });

    navigate(urlPortal);
  };

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={2}>
        <InactivityHandler />
        <Box
          sx={{
            mt: 1,
            mx: { xs: 1.5, md: 4 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant={isMobileOrTablet ? "h4" : "h3"}
            fontWeight={500}
            textAlign="center"
            color={"primary.dark"}
          >
            Selecciona tu moto
          </Typography>

          {isFetchingCatalog ? (
            <CenteredLoader sx={{ mt: 3 }} />
          ) : isMobileOrTablet ? (
            <Box width="100%" mt={3}>
              <Box
                ref={scrollContainerRef}
                onScroll={handleScroll}
                sx={{
                  display: "flex",
                  gap: 2,
                  overflowX: "auto",
                  px: 1,
                  scrollSnapType: "x mandatory",
                  "&::-webkit-scrollbar": { display: "none" },
                  scrollbarWidth: "none",
                }}
              >
                {motorbikeCatalog.map((moto) => (
                  <Box key={moto.id} sx={{ scrollSnapAlign: "center", pb: 1 }}>
                    <CardInformacionMoto item={moto} onSelect={handleSelectMotorbike} />
                  </Box>
                ))}
              </Box>

              <Box display="flex" justifyContent="center" gap={1} mt={1.5}>
                {Array.from({ length: motorbikeCatalog.length }).map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: 9,
                      height: 9,
                      borderRadius: "50%",
                      bgcolor: index === activeIndex ? "primary.light" : "#C9D8F5",
                      transition: "all .2s ease",
                    }}
                  />
                ))}
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                width: "100%",
                mt: 3,
                display: "grid",
                gap: 2,
                gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              }}
            >
              {motorbikeCatalog.map((moto) => (
                <Box key={moto.id} display="flex" justifyContent="center">
                  <CardInformacionMoto item={moto} onSelect={handleSelectMotorbike} />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default SeleccionarMoto;
